import { createTheme, type Theme } from '@mui/material/styles';

/**
 * Main app theme color inventory.
 *
 * These values define the main app's independent color system. The file lives in the shared
 * package because the main app consumes AppThemeProvider directly from shared.
 *
 * Brand anchors
 * - #3B82F6: core light-mode primary; a clear product blue used for principal actions and emphasis.
 * - #1D4ED8: darker blue for stronger action states and deeper contrast.
 * - #60A5FA: lighter primary tint used for highlights and dark-mode primary surfaces.
 * - #6366F1: light-mode secondary accent; indigo that complements the blue primary while adding separation.
 * - #818CF8: dark-mode secondary accent; brighter indigo to stay visible on dark surfaces.
 *
 * Light theme surfaces and text
 * - #F8FAFC: cool off-white page background for a clean, neutral application canvas.
 * - #FFFFFF: white paper background for cards and surfaces that need maximum clarity.
 * - #002D72: deep blue primary text that keeps the main app firmly in its blue identity.
 * - #475569: slate secondary text; neutral enough for supporting copy without pulling focus.
 * - #94A3B8: muted slate disabled text; low emphasis while remaining readable.
 * - #E2E8F0: soft blue-gray divider used for quiet structural separation.
 *
 * Dark theme surfaces and text
 * - #93C5FD: lightest blue tint for elevated highlights in dark mode.
 * - #0E1525: dark page background; deep navy that supports the blue palette without going flat black.
 * - #162036: dark paper background; slightly raised from the page background for cards and panels.
 * - #F1F5F9: near-white primary text for strong readability on dark surfaces.
 * - #253352: dark divider tone that provides structure without visible glare.
 */

// Define custom theme colors/typography here
export const getDesignTokens = (mode: 'light' | 'dark') => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: '#3B82F6', dark: '#1D4ED8', light: '#60A5FA' },
          secondary: { main: '#6366F1' },
          background: { default: '#F8FAFC', paper: '#FFFFFF' },
          text: { primary: '#002D72', secondary: '#475569', disabled: '#94A3B8' },
          divider: '#E2E8F0',
        }
      : {
          primary: { main: '#60A5FA', dark: '#3B82F6', light: '#93C5FD' },
          secondary: { main: '#818CF8' },
          background: { default: '#0E1525', paper: '#162036' },
          text: { primary: '#F1F5F9', secondary: '#94A3B8', disabled: '#475569' },
          divider: '#253352',
        }),
  },
  typography: {
    fontFamily: [
      '"DM Sans"',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  return createTheme(getDesignTokens(mode));
};
